import type { LogicNode, LogicSequenceNode, TradeLogicContextFeatureRef } from '../model';

export interface SequencePriceCaptureSourceOption {
    id: string;
    label: string;
    ref: TradeLogicContextFeatureRef;
}

export interface SequencePriceCaptureTargetOption {
    key: string;
    label: string;
    duration: string;
}

const supportedFeatureKeys = new Set([
    'price.ohlcv.open',
    'price.ohlcv.high',
    'price.ohlcv.low',
    'price.ohlcv.close',
    'price.indicator.ema',
    'price.indicator.sma',
    'price.indicator.vwap'
]);

const sourceId = (ref: TradeLogicContextFeatureRef): string =>
    JSON.stringify({
        featureKey: ref.featureKey,
        duration: ref.duration ?? null,
        selectors: ref.selectors ?? null,
        offsetBars: ref.offsetBars ?? null,
        bindingKey: ref.bindingKey ?? null
    });

const isSupportedSource = (ref: TradeLogicContextFeatureRef): ref is TradeLogicContextFeatureRef & { duration: string } =>
    supportedFeatureKeys.has(ref.featureKey) &&
    typeof ref.duration === 'string' &&
    ref.duration.length > 0 &&
    (ref.bindingKey == null || ref.bindingKey === 'primary') &&
    ref.entityKey == null &&
    ref.entityType == null &&
    !(ref.featureKey === 'price.indicator.vwap' && ['1d', '1w', '1mo'].includes(ref.duration));

export const collectSequenceStepPriceCaptureSources = (node: LogicNode): SequencePriceCaptureSourceOption[] => {
    const refs = new Map<string, TradeLogicContextFeatureRef & { duration: string }>();
    const visit = (current: LogicNode): void => {
        if (current.kind === 'condition' && current.featureRef && isSupportedSource(current.featureRef)) {
            refs.set(sourceId(current.featureRef), current.featureRef);
        }
        if (current.kind === 'group' || current.kind === 'sequence') current.children.forEach(visit);
    };
    visit(node);
    return [...refs.entries()].map(([id, ref]) => ({
        id,
        ref,
        label: `${ref.featureKey.replace(/^price\./, '')} · ${ref.duration}${ref.offsetBars ? ` · ${ref.offsetBars} bars back` : ''}`
    }));
};

export const collectSequencePriceCaptureTargets = (root: LogicNode): SequencePriceCaptureTargetOption[] => {
    const options: SequencePriceCaptureTargetOption[] = [];
    const visit = (node: LogicNode): void => {
        if (node.kind === 'sequence') {
            for (const step of node.children) {
                const capture = node.stepCaptures?.[step.id];
                if (capture && isSupportedSource(capture.sourceRef)) {
                    options.push({
                        key: capture.key,
                        duration: capture.sourceRef.duration,
                        label: `${capture.key} · ${step.label ?? step.id} · ${capture.sourceRef.duration}`
                    });
                }
            }
        }
        if (node.kind === 'group' || node.kind === 'sequence') node.children.forEach(visit);
    };
    visit(root);
    return options;
};

export const findSequencePriceCaptureSource = (
    options: readonly SequencePriceCaptureSourceOption[],
    ref: TradeLogicContextFeatureRef
): SequencePriceCaptureSourceOption | undefined => options.find((option) => option.id === sourceId(ref));

export const withSequenceStepCapture = (
    node: LogicSequenceNode,
    stepId: string,
    capture: NonNullable<LogicSequenceNode['stepCaptures']>[string] | null
): NonNullable<LogicSequenceNode['stepCaptures']> => {
    const next = { ...(node.stepCaptures ?? {}) };
    if (capture) next[stepId] = capture;
    else delete next[stepId];
    return next;
};
